/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import Icon from '@/components/shared/Icons';
import React, { ReactNode } from 'react';
import type { InputRefs } from '@/components/search-bar/SearchBarContainer';

type Props = {
  inputRefs: InputRefs;
  searchTerm: string;
  setIsSearchExpanded: (b: boolean) => void;
  eventStart: string | undefined;
  eventEnd: string | undefined;
  attendees: number | undefined;
  guestRooms: number | undefined;
  children: ReactNode;
  searchToggle: ReactNode;
  strings: Record<string, string>;
};

const styles =
  'flex flex-row items-center justify-center gap-2 px-3 mx-2 my-1 py-1 text-neutral-60 hover:rounded-2xl hover:bg-brand-20 hover:text-neutral-90';

function Spacer() {
  return <div className="h-3 w-0.5 self-center bg-neutral-30" />;
}

export default function CollapsedSearch({
  inputRefs,
  searchTerm,
  setIsSearchExpanded,
  eventStart,
  eventEnd,
  attendees,
  guestRooms,
  searchToggle,
  strings,
  children
}: Props) {
  return (
    <div className="flex w-full flex-col gap-1">
      <div className="ml-1 flex w-full flex-row items-center justify-between">
        <div className="flex w-fit flex-row items-center rounded-2xl border-1 border-solid border-transparent hover:border-brand-60 hover:bg-brand-10">
          {/* collapsed search */}
          <div
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              // expand on enter
              if (e.keyCode === 13) {
                setIsSearchExpanded(true);
              }
            }}
            className="flex w-fit flex-row rounded-full text-b-md"
            onClick={() => setIsSearchExpanded(true)}
            aria-label={strings.searchVenues}
          >
            <div
              className={`${styles} !pl-2`}
              onClick={() => {
                setTimeout(() => {
                  inputRefs.searchTermInputRef.current?.focus();
                }, 0);
              }}
            >
              <Icon id="map-pin" className="h-4 w-4 fill-neutral-60" />
              <span className="line-clamp-1 max-w-[300px] text-neutral-90">{searchTerm}</span>
            </div>
            <Spacer />
            <div
              className={styles}
              onClick={() => {
                setTimeout(() => {
                  inputRefs.startInputRef.current?.click();
                  // adding little delay for search height animation to finish
                }, 300);
              }}
            >
              <Icon id="calendar" className="h-4 w-4 fill-neutral-60" />
              <span className={`${eventStart ? 'text-neutral-90' : ''} line-clamp-1 lg:max-w-[90px] xl:max-w-none`}>
                {eventStart ?? strings.startDate}
              </span>
            </div>
            <Spacer />
            <div
              className={styles}
              onClick={() => {
                setTimeout(() => {
                  inputRefs.endInputRef.current?.click();
                  // adding little delay for search height animation to finish
                }, 300);
              }}
            >
              <Icon id="calendar" className="h-4 w-4 fill-neutral-60" />
              <span className={`${eventEnd ? 'text-neutral-90' : ''} line-clamp-1 lg:max-w-[90px] xl:max-w-none`}>
                {eventEnd ?? strings.endDate}
              </span>
            </div>
            <Spacer />
            <div
              className={styles}
              onClick={() => {
                setTimeout(() => {
                  inputRefs.attendeesInputRef.current?.focus();
                }, 0);
              }}
            >
              <Icon id="people-group" className="h-4 w-4 fill-neutral-60" />
              <span
                className={`${
                  attendees && attendees > 0 ? 'text-neutral-90' : ''
                } line-clamp-1 lg:max-w-[90px] xl:max-w-none`}
              >
                {attendees && attendees > 0 ? attendees : strings.attendees}
              </span>
            </div>
            <Spacer />
            <div
              className={styles}
              onClick={() => {
                setTimeout(() => {
                  inputRefs.guestRoomsInputRef.current?.click();
                }, 0);
              }}
            >
              <Icon id="bed" className="h-4 w-4 fill-neutral-60" />
              <span
                className={`${
                  guestRooms && guestRooms > 0 ? 'text-neutral-90' : ''
                } line-clamp-1 lg:max-w-[90px] xl:max-w-none`}
              >
                {guestRooms && guestRooms > 0 ? guestRooms : strings.guestRooms}
              </span>
            </div>
            <Spacer />
            <div className="pl-4 pr-2 pt-1.5">
              <span className="carina-button rounded-3xl bg-brand-60 p-1" data-cvent-id="search-bar-search-button">
                <Icon id="search" className="h-4 w-4 fill-background" />
              </span>
            </div>
          </div>
        </div>
        {/* AI toggle - desktop */}
        {searchToggle}
      </div>
      {/* filters */}
      {children}
    </div>
  );
}
